<template>
  <nav class="pagination">
    <a
      v-if="currentPage !== 1 && currentPage <= totalPages"
      :href="paginatedUrl(currentPage - 1)"
      aria-label="Previous Page"
      class="pagination-item previous-page"
      @click.prevent="emitChanged(currentPage - 1)"
    />
    <template v-if="isCompact">
      <a
        v-for="item in compactPaginationItems"
        :key="item"
        :href="paginatedUrl(item)"
        :class="{ current: currentPage === item }"
        class="pagination-item"
        @click.prevent="emitChanged(item)"
      >
        {{ item }}
      </a>
    </template>
    <template v-else>
      <component
        v-for="(item, index) in fullPaginationItems"
        :is="item.constructor.name === 'String' ? 'span' : 'a'"
        :key="index"
        :href="paginatedUrl(item)"
        :class="{
          current: currentPage === item,
          dots: item.constructor.name === 'String'
        }"
        class="pagination-item"
        @click.prevent="emitChanged(item)"
      >
        {{ item }}
      </component>
    </template>
    <a
      v-if="currentPage !== totalPages"
      :href="paginatedUrl(currentPage + 1)"
      aria-label="Next Page"
      class="pagination-item next-page"
      @click.prevent="emitChanged(currentPage + 1)"
    />
  </nav>
</template>

<script>
export default {
  name: 'Pagination',
  props: {
    /**
     * A boolean field to determine for rendeing compact pagination.
     * Default value as false.
     *
     * @type {boolean}
     */
    isCompact: {
      type: Boolean,
      default: false
    },
    /**
     * A number field to determine current page of the pagination.
     * Expects a number.
     *
     * @type {number}
     */
    currentPage: {
      type: Number,
      required: true
    },
    /**
     * A string field to determine current page url.
     * Expects a string url.
     *
     * @type {string}
     */
    currentUrl: {
      type: String,
      required: true
    },
    /**
     * A number field to determine the total pages available for the pagination.
     * Expects a number.
     *
     * @type {number}
     */
    totalPages: {
      type: Number,
      required: true
    }
  },
  computed: {
    compactPaginationItems() {
      if (this.totalPages <= 3 || this.currentPage === 1) {
        const length = this.totalPages < 3 ? this.totalPages : 3
        return Array.from({ length }, (_, i) => i + 1)
      } else if (this.currentPage === this.totalPages) {
        return [this.totalPages - 2, this.totalPages - 1, this.totalPages]
      } else {
        return [this.currentPage - 1, this.currentPage, this.currentPage + 1]
      }
    },
    fullPaginationItems() {
      if (this.totalPages <= 5) {
        const length = this.totalPages
        return Array.from({ length }, (_, i) => i + 1)
      } else if (
        this.currentPage === 1 ||
        this.currentPage === this.totalPages
      ) {
        return [1, 2, '...', this.totalPages - 1, this.totalPages]
      }

      const endingPoint = [
        this.totalPages - 2,
        this.totalPages - 1,
        this.totalPages
      ]

      if (this.currentPage <= 3) {
        return [1, 2, 3, 4, '...', this.totalPages]
      } else if (endingPoint.includes(this.currentPage)) {
        return [1, '...', this.totalPages - 3, ...endingPoint]
      } else {
        return [
          1,
          '...',
          this.currentPage - 1,
          this.currentPage,
          this.currentPage + 1,
          '...',
          this.totalPages
        ]
      }
    }
  },
  methods: {
    emitChanged(value) {
      if (this.currentPage === value || isNaN(Number(value))) return

      this.$emit('changed', value)
    },
    paginatedUrl(pageNumber) {
      const [url, queryStrings] = this.currentUrl.split('?')
      const params = new URLSearchParams(queryStrings)
      const pageNumberKey = 'page'
      params.delete(pageNumberKey)

      if (pageNumber > 1) {
        params.set(pageNumberKey, pageNumber)
      }
      // decodeURI: make page%5Bsize%5D=30 -> page[size]=30
      const decodedQueryStrings = decodeURI(params.toString())

      if (decodedQueryStrings.length === 0) {
        return url
      }

      return `${url}?${decodedQueryStrings}`
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~bulma/sass/components/level';

.pagination {
  display: flex;
  text-align: center;

  &-item {
    align-items: center;
    border: 1px solid $grey;
    display: flex;
    height: 40px;
    justify-content: center;
    width: 40px;

    &.current {
      background-color: $grey-light;
      color: $black;
      cursor: default;
    }

    &.dots {
      border-top: 0;
      border-bottom: 0;
    }

    &:not(&.dots):not(&.current):hover {
      background-color: $grey;
      color: $black;
    }

    &.previous-page,
    &.next-page {
      background-repeat: no-repeat;
      background-size: 15px 15px;
      background-position: center center;
    }

    &.previous-page {
      background-image: url('./assets/arrow-left.svg');
    }

    &.next-page {
      background-image: url('./assets/arrow-right.svg');
    }

    &:not(:first-child) {
      border-left: 0;
    }
  }
}
</style>
